import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { Seo } from "@/components/Seo";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const BlogListPage = () => {
  const [posts, setPosts] = useState(null);
  const [query, setQuery] = useState("");
  const [category, setCategory] = useState("");

  useEffect(() => {
    const params = new URLSearchParams();
    if (query) params.set("q", query);
    if (category) params.set("category", category);
    axios.get(`${API}/blog?${params.toString()}`).then((res) => setPosts(res.data)).catch(() => setPosts([]));
  }, [query, category]);

  const categories = Array.from(new Set((posts || []).map((p) => p.category)));

  return (
    <>
      <Seo
        title="Journal — #whatshesaidproject"
        description="Essays and editorial writing from #whatshesaidproject on story, craft, culture, and the platforms that carry them."
        path="/blog"
      />
      <section className="bg-white text-[#121212] pt-12 pb-24 md:py-28 border-b border-[#e5e5e5]">
        <div className="max-w-6xl mx-auto px-6">
          <span className="text-xs font-mono uppercase tracking-[.18em] text-[#e02020] block mb-3">
            Journal
          </span>
          <h1
            data-testid="blog-headline"
            className="font-disp text-[clamp(36px,5vw,72px)] leading-[1.02] tracking-tight font-normal text-[#121212] text-balance max-w-4xl mb-6"
          >
            Essays &amp; editorial writing.
          </h1>
          <p className="text-[17px] leading-[1.65] text-[#555555] max-w-2xl mb-6">
            Writing on story, craft, culture, and the platforms that carry them.
          </p>
          <a
            href="https://shannonivey.substack.com/"
            target="_blank"
            rel="noopener noreferrer"
            data-testid="journal-substack-link"
            className="text-xs font-mono uppercase tracking-[.14em] text-[#121212] underline underline-offset-4 hover:text-[#e02020] transition-colors"
          >
            Read on Substack →
          </a>
        </div>
      </section>

      <section className="bg-white text-[#121212] py-24 md:py-32">
        <div className="max-w-6xl mx-auto px-6">
          <div className="flex flex-col md:flex-row gap-4 md:items-center md:justify-between mb-12 pb-6 border-b border-[#121212]">
            <input
              type="search"
              data-testid="blog-search-input"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search the archive…"
              className="w-full md:max-w-sm bg-white border border-[#e5e5e5] px-4 py-3 text-sm focus:border-[#121212] focus:outline-none rounded-none"
            />
            {categories.length > 0 && (
              <div className="flex flex-wrap gap-3" data-testid="blog-category-filters">
                <button
                  type="button"
                  onClick={() => setCategory("")}
                  className={`text-xs font-mono uppercase tracking-[.14em] px-3 py-2 border transition-colors rounded-none ${
                    category === ""
                      ? "border-[#121212] bg-[#121212] text-white"
                      : "border-[#e5e5e5] text-[#555555] hover:border-[#121212]"
                  }`}
                >
                  All
                </button>
                {categories.map((c) => (
                  <button
                    key={c}
                    type="button"
                    onClick={() => setCategory(c)}
                    data-testid={`blog-filter-${c.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                    className={`text-xs font-mono uppercase tracking-[.14em] px-3 py-2 border transition-colors rounded-none ${
                      category === c
                        ? "border-[#121212] bg-[#121212] text-white"
                        : "border-[#e5e5e5] text-[#555555] hover:border-[#121212]"
                    }`}
                  >
                    {c}
                  </button>
                ))}
              </div>
            )}
          </div>

          {posts === null ? (
            <p className="text-xs font-mono uppercase tracking-[.14em] text-[#555555]">Loading…</p>
          ) : posts.length === 0 ? (
            <div className="border border-dashed border-[#cccccc] p-16 text-center" data-testid="blog-empty-state">
              <p className="font-disp text-2xl text-[#121212] mb-3">The journal is opening soon.</p>
              <p className="text-sm text-[#555555]">
                Essays, notes on craft, and cultural commentary will be published here ahead of launch.
              </p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {posts.map((post) => (
                <Link
                  key={post.id}
                  to={`/blog/${post.slug}`}
                  data-testid={`blog-card-${post.slug}`}
                  className="group border border-[#e5e5e5] hover:border-[#121212] transition-colors bg-white flex flex-col"
                >
                  {post.featured_image && (
                    <figure className="relative aspect-[16/10] bg-[#f4f4f4] border-b border-[#e5e5e5] overflow-hidden">
                      <img
                        src={post.featured_image}
                        alt={post.title}
                        className="absolute inset-0 w-full h-full object-cover"
                      />
                    </figure>
                  )}
                  <div className="p-6 flex flex-col flex-1">
                    <div className="flex items-center gap-3 text-xs font-mono text-[#6b6b6b] mb-3">
                      <span>
                        {post.published_at
                          ? new Date(post.published_at).toLocaleDateString("en-US", {
                              month: "short",
                              day: "numeric",
                              year: "numeric",
                            })
                          : ""}
                      </span>
                      <span>&bull;</span>
                      <span className="text-[#e02020] uppercase tracking-[.14em]">{post.category}</span>
                    </div>
                    <h2 className="font-disp text-2xl text-[#121212] group-hover:text-[#e02020] transition-colors mb-3">
                      {post.title}
                    </h2>
                    {post.excerpt && (
                      <p className="text-sm text-[#555555] leading-relaxed">{post.excerpt}</p>
                    )}
                  </div>
                </Link>
              ))}
            </div>
          )}
        </div>
      </section>
    </>
  );
};

export default BlogListPage;
